import { __decorate } from "tslib";
import { action } from 'mobx';
import isObject from 'lodash/isObject';
import noop from 'lodash/noop';
function on(el, eventName, handle, handles) {
    if (el.addEventListener) {
        const [fn, options] = handle;
        el.addEventListener(eventName, fn, options);
    }
    else {
        const delegates = [];
        handles.forEach(([, , delegateFn]) => {
            if (el.detachEvent) {
                el.detachEvent(`on${eventName}`, delegateFn);
                delegates.unshift(delegateFn);
            }
        });
        delegates.forEach(delegateFn => {
            if (el.attachEvent) {
                el.attachEvent(`on${eventName}`, delegateFn);
            }
        });
    }
}
function off(el, eventName, handle) {
    const [fn, options, delegateFn] = handle;
    if (el.removeEventListener) {
        el.removeEventListener(eventName, fn, options);
    }
    else if (el.detachEvent) {
        el.detachEvent(`on${eventName}`, delegateFn);
    }
}
function isEventListenerOptions(options) {
    return isObject(options);
}
function isAddEventListenerOptions(options) {
    return isEventListenerOptions(options) && ('once' in options || 'passive' in options);
}
function getCapture(options) {
    return isEventListenerOptions(options) ? options.capture || false : options;
}
function isSameHandler(handle, other) {
    const [handleFn, handleOption] = handle;
    const [otherFn, otherOption] = other;
    return handleFn === otherFn && getCapture(handleOption) === getCapture(otherOption);
}
function callHandler(events, handle, ...rest) {
    const [, options, delegateFn] = handle;
    if (isAddEventListenerOptions(options) && options.once) {
        const index = events.indexOf(handle);
        if (index !== -1) {
            events.splice(index, 1);
        }
    }
    return delegateFn(...rest);
}
function delegate(fn) {
    if ('handleEvent' in fn) {
        return (...rest) => fn.handleEvent(...rest);
    }
    return (...rest) => fn(...rest);
}
export default class EventManager {
    constructor(el) {
        this.events = {};
        this.el = el;
    }
    addEventListener(eventName, fn, options = false) {
        eventName = eventName.toLowerCase();
        const events = this.events[eventName] || [];
        const index = events.findIndex((handle) => isSameHandler(handle, [fn, options, noop]));
        if (index === -1) {
            const newHandle = [fn, options, delegate(fn)];
            if (getCapture(options)) {
                const captureIndex = events.findIndex(([, handleOptions]) => !getCapture(handleOptions));
                if (captureIndex === -1) {
                    events.push(newHandle);
                }
                else {
                    events.splice(captureIndex, 0, newHandle);
                }
            }
            else {
                events.push(newHandle);
            }
            this.events[eventName] = events;
            const { el } = this;
            if (el) {
                on(el, eventName, newHandle, events);
            }
        }
        return this;
    }
    removeEventListener(eventName, fn, options = false) {
        eventName = eventName.toLowerCase();
        const events = this.events[eventName];
        if (events) {
            const { el } = this;
            if (fn) {
                const index = events.findIndex(handle => isSameHandler(handle, [fn, options, noop]));
                if (index !== -1) {
                    if (el) {
                        off(el, eventName, events[index]);
                    }
                    events.splice(index, 1);
                }
            }
            else {
                this.events[eventName] = el
                    ? (this.events[eventName] || []).filter((handle) => {
                        off(el, eventName, handle);
                        return false;
                    })
                    : [];
            }
        }
        return this;
    }
    fireEventSync(eventName, ...rest) {
        const events = this.events[eventName.toLowerCase()];
        return events ? [...events].every(handle => callHandler(events, handle, ...rest) !== false) : true;
    }
    fireEvent(eventName, ...rest) {
        const events = this.events[eventName.toLowerCase()];
        return events
            ? Promise.all([...events].map((handle) => callHandler(events, handle, ...rest))).then(all => all.every(result => result !== false))
            : Promise.resolve(true);
    }
    clear() {
        if (this.el) {
            Object.keys(this.events).forEach(eventName => this.removeEventListener(eventName));
        }
        this.events = {};
        return this;
    }
}
__decorate([
    action
], EventManager.prototype, "fireEventSync", null);
__decorate([
    action
], EventManager.prototype, "fireEvent", null);
export function preventDefault(e) {
    e.preventDefault();
}
export function stopPropagation(e) {
    e.stopPropagation();
}
export function stopEvent(e) {
    preventDefault(e);
    stopPropagation(e);
}
